import { KalturaAssetReferenceType, KalturaAssetType } from '@kaltura-ott/ott-client-types/api/types';
import {
  AdditionalMediaType,
  AssetPresentationRatioEnum,
  createFakeCategoryTree,
  getAssetByIdOrExternalId,
  getHeightByWidth,
  getRatioByType,
  getRecordingProgram,
  getResizerUrl,
  getSeriesAsset,
  getWidthByHeight,
  KuxEpisodeType,
  KuxImageRail,
  KuxLinearType,
  KuxMediaAssetType,
  KuxMovieType,
  KuxProgramType,
  KuxSeriesType,
  MediaType,
  getMosaicProgramAssetByExternalId,
} from '@kaltura-ott/tvpil';
import { action, computed, observable } from 'mobx';
import { lastValueFrom } from 'rxjs';
import { container, injectable } from 'tsyringe';

import { ParentalPinType } from '../../../types/stores/parental-pin-store-type';
import { featureEnabler } from '../../../utils';
import { assertPresence, errorHandling } from '../../../utils/error-handling';
import type { AppStore } from '../../app/app-store';
import { DmsStore } from '../../dms';
import { Episode, Linear, Movie, Program, Series, SeriesItemType, ViewableEntitiesType } from '../../entities';
import {
  addBookmarkToPlayable,
  addBookmarkToRecords,
  addCachedPurchaseToPlayable,
  addCurrentProgramToLinear,
  addLinearsToCatchupSeries,
  addPriceToPurchasedPlayable,
  addRentPossibilitiesToPlayable,
  addSubscriptionPossibilitiesToPlayable,
  checkProductPriceStatus,
  creatorEntityStore,
} from '../../entities/entities.service';
import { RecordRepository } from '../../recordRepository';
import { PlayoutStoreV2 } from '../playout';
import { RecordAdditionalRailStoreV2 } from '../widgets/rails/record-rail/record-rail-additional-store';

import { AssetDetailsPage, AssetDetailsPageStore } from './asset-details-page-store';
import { SeriesAdditionalRailStoreV2 } from './series-additional-rail-store-v2';

const MAX_CACHE_SIZE = 3;
export interface MainImageProperty {
  originUrl: string | undefined;
  isLandscape: boolean;
  ratio?: AssetPresentationRatioEnum;
  type?: number;
}

export type DetailsStoreInitArgs = {
  id?: number;
  externalId?: string;
  type?: MediaType | AdditionalMediaType;
  isDetailsSeries?: boolean;
  asset?: ViewableEntitiesType | Linear | null;
  isRecord?: boolean;
  seriesId?: string;
  useCache?: boolean;
  fromPlayoutStore?: boolean;
  loadMosaicAsset?: boolean;
};

type DetailsCache = {
  timestamp: number;
  value: {
    asset?: ViewableEntitiesType;
    seriesAdditionalRail?: SeriesAdditionalRailStoreV2;
    scheduledRecordingSeriesRail?: RecordAdditionalRailStoreV2;
    assetDetailsPage?: AssetDetailsPage;
  };
};

/**
 * Details page store for program and vod
 * @category Store
 */
@injectable()
export class DetailsStore {
  static moduleName = 'DetailsStore';

  @observable asset?: ViewableEntitiesType;
  @observable mosaicAsset?: ViewableEntitiesType;
  @observable seriesAdditionalRail?: SeriesAdditionalRailStoreV2;
  @observable scheduledRecordingSeriesRail?: RecordAdditionalRailStoreV2;
  @observable assetDetailsPage?: AssetDetailsPage;
  @observable isAssetDataReady = false;
  @observable isAdditionalDataReady = false;
  @observable externalPortalUrl?: string;

  private assetDetailsPageStore?: AssetDetailsPageStore;
  private cache: { [key: string]: DetailsCache } = {};

  private get playoutStore() {
    return container.resolve<PlayoutStoreV2>('playoutStore');
  }
  private get dmsStore() {
    return container.resolve<DmsStore>('dmsStore');
  }
  /**
   *
   * @param id - id of asset
   * @param externalId - externalId of asset
   * @param type - mediaType of asset
   * @param isDetailsSeries - true for Series and Catch-Ups
   * @param asset - ViewableEntitiesType asset
   * @param isRecord - true for records
   * @param seriesId - id of Series if loaded from clicking on Episode card
   * @param useCache - boolean
   */
  @action
  @errorHandling()
  async init({
    id,
    type,
    isDetailsSeries,
    asset,
    isRecord,
    seriesId,
    useCache,
    fromPlayoutStore,
    externalId,
    loadMosaicAsset,
  }: DetailsStoreInitArgs): Promise<void> {
    this.resetStore();
    const appStore = container.resolve<AppStore>('appStore');
    this.assetDetailsPageStore = assertPresence(appStore.rootStore).assetDetailsPages;

    if (!fromPlayoutStore && !asset && !((id || externalId) && type) && !seriesId) {
      throw new Error('id, externalId or asset must be setup');
    }

    this.isAssetDataReady = false;
    this.isAdditionalDataReady = false;

    const cacheKey = `${seriesId || id || asset?.id}_${isDetailsSeries}_${externalId}`;

    if (useCache && cacheKey && this.cache[cacheKey]) {
      const cache = this.cache[cacheKey];

      if (cache.value) {
        const cachedDetails = cache.value;
        Object.assign(this, cachedDetails);
        cache.timestamp = Date.now();
        this.isAssetDataReady = true;
        await this.loadAdditionalAssetData(isRecord);
        return;
      }
    }

    if (fromPlayoutStore) {
      if (this.playoutStore.currentAsset instanceof Linear) {
        this.assetDetailsPage = this.assetDetailsPageStore.getByType(MediaType.Program);
        this.asset = Object.assign(
          new Program(this.playoutStore.currentAsset.currentProgram?.data as KuxProgramType),
          this.playoutStore.currentAsset.currentProgram,
        );
      } else if (this.playoutStore.currentAsset) {
        this.assetDetailsPage = this.assetDetailsPageStore.getByType(this.playoutStore.currentAsset.data.mediaType);

        this.asset = Object.assign(
          // @ts-expect-error TODO Fix types
          new this.playoutStore.currentAsset.constructor(this.playoutStore.currentAsset.data),
          this.playoutStore.currentAsset,
        );
      }
      this.isAssetDataReady = true;
      await Promise.all([this.addBookmark(isRecord)]);
      this.isAdditionalDataReady = true;
      return;
    }

    if (asset instanceof Linear) {
      this.assetDetailsPage = this.assetDetailsPageStore.getByType(MediaType.Program);
      await this.setInternalId({ id: asset.id, type: MediaType.Linear, isRecord, externalId });
    } else {
      if (asset && !seriesId) {
        this.asset = asset;
        this.assetDetailsPage = this.assetDetailsPageStore.getByType(asset.data.mediaType);
        this.resetAdultPinCode();
        this.isAssetDataReady = true;
        await this.loadAdditionalAssetData(isRecord);

        if (this.asset instanceof Program && loadMosaicAsset) {
          this.mosaicAsset = await this.getMosaicAsset(this.asset);
        }
      } else if ((id || externalId) && type) {
        this.assetDetailsPage = this.assetDetailsPageStore.getByType(type);
        await this.setInternalId({ id, type, isRecord, seriesId, isDetailsSeries, externalId, loadMosaicAsset });
      } else {
        this.assetDetailsPage = this.assetDetailsPageStore.getDefault();
        this.isAssetDataReady = true;
        this.isAdditionalDataReady = true;
      }
    }

    this.externalPortalUrl = this.assetDetailsPage?.properties.externalPortalUrl;

    if (this.asset instanceof Series && this.asset.data.seriesId && isDetailsSeries) {
      const episodeDetailsPage = assertPresence(this.assetDetailsPageStore.getByType(MediaType.Episode));

      this.seriesAdditionalRail = new SeriesAdditionalRailStoreV2(episodeDetailsPage.tree, createFakeCategoryTree());

      await this.seriesAdditionalRail.loadSeasonList(
        this.asset.data.seriesId,
        this.dmsStore.dms.mediaTypes[MediaType.Episode],
      );
      await this.seriesAdditionalRail.setupAssetType(MediaType.Episode);
      await this.seriesAdditionalRail.setupSeasonNumber();
    }

    if (this.asset instanceof Program && this.asset.data.seriesId && isDetailsSeries && !isRecord) {
      this.seriesAdditionalRail = new SeriesAdditionalRailStoreV2(this.assetDetailsPage.tree, createFakeCategoryTree());

      await this.seriesAdditionalRail.loadSeasonList(this.asset.data.seriesId, KalturaAssetType.epg);
      await this.seriesAdditionalRail.setupAssetType(MediaType.Program);
      await this.seriesAdditionalRail.setupSeasonNumber();
    }

    if (isRecord && isDetailsSeries && this.asset instanceof Program && appStore.rootStore?.seriesRecordingPageTree) {
      // for single recordings episodes
      this.seriesAdditionalRail = new SeriesAdditionalRailStoreV2(
        appStore.rootStore.seriesRecordingPageTree.children[0],
        appStore.rootStore.seriesRecordingPageTree,
      );

      await this.seriesAdditionalRail?.setupAssetType(AdditionalMediaType.Recording);
      await this.seriesAdditionalRail?.loadSeasonList(
        this.asset.data.seriesId,
        AdditionalMediaType.Recording as unknown as KalturaAssetType,
      );
      await this.seriesAdditionalRail?.setupSeasonNumber();

      // for scheduled recordings series episodes
      this.scheduledRecordingSeriesRail = new RecordAdditionalRailStoreV2(
        appStore.rootStore.seriesRecordingPageTree.children[0],
        appStore.rootStore.seriesRecordingPageTree,
      );
      await this.scheduledRecordingSeriesRail?.setupSeriesId(this.asset.data?.seriesId);
    }

    if (isRecord) {
      const recordRepository = container.resolve<RecordRepository>('recordRepository');
      recordRepository.saveCurrentAssetId(this?.asset?.id);
    }

    if (cacheKey) {
      this.saveDataToCache(cacheKey);
    }
  }

  get isLandscape(): boolean | undefined {
    if (!this.assetDetailsPage) {
      return;
    }

    let ratio: AssetPresentationRatioEnum | undefined;
    const type = this.assetDetailsPage.properties.assetPresentationImageType;

    if (type) {
      ratio = getRatioByType(type);
    } else {
      ratio = this.assetDetailsPage.properties.presentationRatio;
    }

    if (!ratio) {
      ratio = this.assetDetailsPage.properties.presentationRatio;
    }

    const [horizontalRatio, verticalRatio] = ratio.split(/:|x/);

    if (Number(horizontalRatio) > Number(verticalRatio)) {
      return true;
    }

    return false;
  }

  /**
   * Method returns the main image for detail page
   * @param landscapeWidth - number
   * @param portraitHeight - number
   * @returns KuxImageRail
   */
  public detailImage(landscapeWidth: number, portraitHeight: number): KuxImageRail | undefined {
    return computed(() => {
      if (!this.assetDetailsPage) {
        return;
      }
      const type = this.assetDetailsPage.properties.assetPresentationImageType;
      let ratio: AssetPresentationRatioEnum | undefined;
      let originImageUrl: string | undefined;

      if (type) {
        ratio = getRatioByType(type);
        originImageUrl = this.asset?.data.images[type]?.url;
      } else {
        ratio = this.assetDetailsPage.properties.presentationRatio;
        originImageUrl = this.asset?.data.images[ratio]?.url;
      }

      if (!ratio) {
        ratio = this.assetDetailsPage.properties.presentationRatio;
      }

      if (this.isLandscape) {
        const landscapeHeight = getHeightByWidth(landscapeWidth, ratio);
        return {
          height: landscapeHeight,
          width: landscapeWidth,
          originImageUrl,
          imageUrl: originImageUrl && getResizerUrl(originImageUrl, landscapeHeight, landscapeWidth),
        };
      } else {
        const portraitWidth = getWidthByHeight(landscapeWidth, ratio);
        return {
          height: portraitHeight,
          width: portraitWidth,
          originImageUrl,
          imageUrl: originImageUrl && getResizerUrl(originImageUrl, portraitHeight, portraitWidth),
        };
      }
    }).get();
  }
  get ratio() {
    if (!this.assetDetailsPage) {
      return;
    }

    const type = this.assetDetailsPage.properties.assetPresentationImageType;

    if (type) {
      return getRatioByType(type);
    }

    return this.assetDetailsPage.properties.presentationRatio;
  }

  @computed get mainImageProperty(): MainImageProperty | undefined {
    if (!this.asset || !this.assetDetailsPage) {
      return;
    }
    const type = this.assetDetailsPage.properties.assetPresentationImageType;

    if (type) {
      const ratio = getRatioByType(type);

      return {
        originUrl: this.asset.data.images[type]?.url,
        isLandscape: !!this.isLandscape,
        type,
        ratio,
      };
    } else {
      const ratio = this.assetDetailsPage.properties.presentationRatio;

      return {
        originUrl: this.asset.data.images[ratio]?.url,
        isLandscape: !!this.isLandscape,
        ratio,
      };
    }
  }

  @computed get firstEpisode(): SeriesItemType | undefined {
    if (!this.seriesAdditionalRail) {
      return;
    }

    return this.seriesAdditionalRail.firstEpisode;
  }

  private addBookmark(isRecord?: boolean) {
    const promises = [];
    if (this.asset instanceof Program) {
      if (isRecord) {
        promises.push(addBookmarkToRecords([this.asset]));
      } else {
        promises.push(addBookmarkToPlayable([this.asset]));
      }
    }
    return promises;
  }

  public async loadAdditionalAssetData(isRecord = false, isDetailsSeries = false) {
    const promises = [];
    const appStore = container.resolve<AppStore>('appStore');

    if (this.asset instanceof Program) {
      await addPriceToPurchasedPlayable(this.asset);

      if (!this.asset.isPurchased) {
        promises.push(addSubscriptionPossibilitiesToPlayable(this.asset, this.asset.price));
      }

      promises.push(...this.addBookmark(isRecord));

      if (isDetailsSeries && this.asset.data.seriesId && !isRecord) {
        promises.push(addLinearsToCatchupSeries(this.asset));
      }
    }

    if (this.asset instanceof Movie || this.asset instanceof Episode) {
      await addPriceToPurchasedPlayable(this.asset);

      if (!this.asset.isProviderAsset) {
        promises.push(addBookmarkToPlayable([this.asset], KalturaAssetType.media));

        if (!this.asset.isPurchased) {
          promises.push(addRentPossibilitiesToPlayable([this.asset]));
          promises.push(addSubscriptionPossibilitiesToPlayable(this.asset, this.asset.price));
        }
      } else if (!this.asset.isAssetPlayOnExternalApp) {
        promises.push(addBookmarkToPlayable([this.asset], KalturaAssetType.media));
      }
    }

    await Promise.all(promises);

    if (
      !this.asset?.isPurchased &&
      (this.asset instanceof Movie ||
        this.asset instanceof Episode ||
        (this.asset instanceof Program && !this.asset.isProgramRecord))
    ) {
      await addCachedPurchaseToPlayable(this.asset);
    }

    if (this.asset instanceof Movie || this.asset instanceof Episode || this.asset instanceof Program) {
      await checkProductPriceStatus(this.asset)?.then((forceUpdateCache) => {
        if (forceUpdateCache) {
          appStore.resetWidgets();
          appStore.reInitEpg();
        }
      });
    }
    this.isAdditionalDataReady = true;
  }

  private async getMosaicAsset(asset: Program): Promise<Program | undefined> {
    const appStore = container.resolve<AppStore>('appStore');

    if (
      asset?.data.mosaicInfo?.isMosaicEvent === '1' &&
      (asset?.data.mosaicInfo.type === 'video' || asset?.data.mosaicInfo.type === 'hidden') &&
      asset?.data.mosaicInfo.mosaicProgramExternalId &&
      appStore.dms.mosaicInfo.isMosaicSupported
    ) {
      const programType = await lastValueFrom(
        getMosaicProgramAssetByExternalId(asset?.data.mosaicInfo.mosaicProgramExternalId),
      );

      if (programType) {
        const programAsset = creatorEntityStore(programType);

        return programAsset;
      }
    }

    return;
  }

  private async setInternalId({
    id,
    externalId,
    type,
    isRecord,
    seriesId,
    isDetailsSeries,
    loadMosaicAsset,
  }: {
    id?: number;
    externalId?: string;
    type: MediaType | AdditionalMediaType;
    isRecord?: boolean;
    seriesId?: string;
    isDetailsSeries?: boolean;
    loadMosaicAsset?: boolean;
  }): Promise<void> {
    let asset: KuxMediaAssetType | undefined;

    if (seriesId && !isRecord) {
      asset = await lastValueFrom(getSeriesAsset(seriesId));
    } else if (type === MediaType.Linear && (id || externalId)) {
      const kuxLinear = (await lastValueFrom(
        getAssetByIdOrExternalId<KuxLinearType>({
          id,
          externalId,
        }),
      )) as KuxLinearType;
      if (!kuxLinear) {
        return;
      }
      const linear = creatorEntityStore(kuxLinear);
      await addCurrentProgramToLinear([linear], undefined, true);
      this.asset = linear.currentProgram;
    } else if (type === MediaType.Program) {
      if (isRecord && id) {
        const ids = isDetailsSeries ? [seriesId] : [id.toString()];
        asset = await lastValueFrom(
          getRecordingProgram(ids, KalturaAssetType.epg, isDetailsSeries, featureEnabler.variation('NEW_NPVR', false)),
        );
      } else {
        const program = (await lastValueFrom(
          getAssetByIdOrExternalId<KuxProgramType>({
            id,
            assetReferenceType: KalturaAssetReferenceType.epgInternal,
            externalId,
          }),
        )) as KuxProgramType;
        asset = program;
      }
    } else {
      asset = (await lastValueFrom(
        getAssetByIdOrExternalId<KuxMovieType | KuxEpisodeType | KuxSeriesType>({
          id,
          assetReferenceType: KalturaAssetReferenceType.media,
          externalId,
        }),
      )) as KuxProgramType;
    }
    if (asset) {
      this.asset = creatorEntityStore(asset) as ViewableEntitiesType;

      if (this.asset instanceof Program && loadMosaicAsset) {
        this.mosaicAsset = await this.getMosaicAsset(this.asset);
      }
    }

    this.resetAdultPinCode();

    this.isAssetDataReady = true;

    await this.loadAdditionalAssetData(isRecord, isDetailsSeries);
  }

  /**
   * used for TVOD expiration time label only
   */

  @computed get expirationTime() {
    if ((this.asset instanceof Movie || this.asset instanceof Episode) && !this.asset?.isRented) {
      return undefined;
    }
    const expirationTime = this.asset?.price?.endDate ? this.asset?.price?.endDate * 1000 - Date.now() : undefined;
    const boundary = this.assetDetailsPage?.properties.rentalBadgeBoundary
      ? this.assetDetailsPage.properties.rentalBadgeBoundary * 3600000
      : undefined;

    if (expirationTime && boundary && expirationTime < boundary) {
      return expirationTime;
    }
  }

  @action
  resetStore() {
    this.isAssetDataReady = false;
    this.isAdditionalDataReady = false;
    this.asset = undefined;
    this.assetDetailsPage = undefined;
    if (this.seriesAdditionalRail) {
      this.seriesAdditionalRail = undefined;
    }
    if (this.scheduledRecordingSeriesRail) {
      this.scheduledRecordingSeriesRail = undefined;
    }
  }

  clearCache() {
    this.cache = {};
  }

  private resetAdultPinCode() {
    const appStore = container.resolve<AppStore>('appStore');

    if (!appStore.params.useParentalPinStoreDepreciated) {
      if (!this.asset?.isAdult) {
        appStore.parentalPinStore.originalStore.clearParentalPin(ParentalPinType.adult);
      }

      appStore.parentalPinStore.originalStore.lastParentalPinEnteredValue = Math.min(
        appStore.parentalPinStore.originalStore.lastParentalPinEnteredValue,
        this.asset?.parentalRating || 0,
      );
    }
  }

  private saveDataToCache(cacheKey: string | number) {
    this.cache[cacheKey] = {
      value: {
        asset: this.asset,
        scheduledRecordingSeriesRail: this.scheduledRecordingSeriesRail,
        seriesAdditionalRail: this.seriesAdditionalRail,
        assetDetailsPage: this.assetDetailsPage,
      },
      timestamp: Date.now(),
    };

    if (Object.keys(this.cache).length > MAX_CACHE_SIZE) {
      const oldest = Object.entries(this.cache).sort((entry1, entry2) => entry1[1]?.timestamp - entry2[1].timestamp)[0];
      delete this.cache[oldest[0]];
    }
  }
}
